import { useState } from "react";
import { Plus } from "lucide-react";
import {
  SkillFormDialog,
  SkillFormDialogMode,
  type SkillFormValues,
  type SkillSubmitResult,
} from "~/components/assistant/SkillFormDialog";
import SkillPickerModal from "~/components/assistant/SkillPickerModal";
import { SkillRow } from "~/components/assistant/SkillRow";
import type { LibrarySkill } from "~/components/assistant/hooks/useSkillLibrary";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import useUser from "~/contexts/user";
import { ConfirmDestructiveDialog } from "~/pages/manifold-nextgen/components/ConfirmDestructiveDialog";
import { useSkillMutations } from "~/pages/manifold-nextgen/components/conversations/skills/useSkillMutations";
import { SettingsGroup } from "~/pages/manifold-nextgen/components/settings/SettingsGroup";
import { SettingsGroupState } from "~/pages/manifold-nextgen/components/settings/SettingsGroupState";
import { SettingsPage } from "~/pages/manifold-nextgen/components/settings/SettingsPage";
import { SKILL_NAME_MAX_LENGTH, type PartialSkill, type Skill } from "~/services/skills";
import {
  extractErrorCode,
  getApiErrorMessage,
  isApiError,
  parseApiErrorBody,
} from "~/utils/apiClient";
import { useUserSkills } from "./useUserSkills";

type SkillDialogContent =
  | { kind: "none" }
  | { kind: "picker" }
  | { kind: "create"; initialData?: Partial<SkillFormValues>; sourceName?: string }
  | { kind: "edit"; skill: Skill };

/**
 * The profile's Skills tab: the user's personal USER-scope skill library, which
 * every conversation they start can draw on.
 */
export const ProfileSkillsTab = () => {
  const user = useUser();
  const { showSuccess, showError } = useToast();

  const { skills, isLoading: skillsLoading, error: skillsError } = useUserSkills(user?.email);
  const { createUserSkill, updateSkill, deleteSkill, getSkill } = useSkillMutations();

  const [dialogContent, setDialogContent] = useState<SkillDialogContent>({ kind: "none" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PartialSkill | null>(null);

  const closeDialog = () => setDialogOpen(false);

  const handleEditSkill = async (skillId: string) => {
    try {
      const skill = await getSkill(skillId);
      setDialogContent({ kind: "edit", skill });
      setDialogOpen(true);
    } catch {
      showError("Unable to load skill for editing");
    }
  };

  const handlePickFromLibrary = async (skill: LibrarySkill) => {
    try {
      const sourceSkill = await getSkill(skill.id);
      setDialogContent({
        kind: "create",
        sourceName: sourceSkill.name,
        initialData: {
          name: `${sourceSkill.name}-copy`.slice(0, SKILL_NAME_MAX_LENGTH),
          description: sourceSkill.description,
          content: sourceSkill.content,
        },
      });
    } catch {
      showError("Unable to load skill from the library");
    }
  };

  const handleCreateNew = () => setDialogContent({ kind: "create" });

  const handleEditorSubmit = async (data: SkillFormValues): Promise<SkillSubmitResult> => {
    try {
      if (dialogContent.kind === "edit") {
        await updateSkill(dialogContent.skill.id, data);
        showSuccess(`"${data.name}" updated`);
      } else if (dialogContent.kind === "create") {
        await createUserSkill(data);
        showSuccess(`"${data.name}" added`);
      } else {
        return { ok: false };
      }
      closeDialog();
      return { ok: true };
    } catch (e) {
      if (isApiError(e) && e.status === 409) {
        const code = extractErrorCode(parseApiErrorBody(e));
        if (code === "conflict" || code === null) {
          const message = "A skill with this name already exists in your library.";
          showError(`${message} Choose a different name.`);
          return { ok: false, field: "name", message };
        }
      }
      showError(isApiError(e) ? getApiErrorMessage(e) : "Unable to save skill");
      return { ok: false };
    }
  };

  const handleDeleteSkillConfirm = async (skill: PartialSkill) => {
    try {
      await deleteSkill(skill);
      showSuccess(`"${skill.name}" removed`);
    } catch (e) {
      showError("Unable to remove skill");
      throw e;
    }
  };

  const skillFormInitialData =
    dialogContent.kind === "edit"
      ? {
          name: dialogContent.skill.name,
          description: dialogContent.skill.description ?? "",
          content: dialogContent.skill.content,
        }
      : dialogContent.kind === "create"
        ? dialogContent.initialData
        : undefined;

  return (
    <SettingsPage
      title="Skills"
      description="Persistent, reusable instructions. Any agent you use can access them."
    >
      <SettingsGroup
        label="Your skills"
        count={skills.length}
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDialogContent({ kind: "picker" });
              setDialogOpen(true);
            }}
            className="text-primary hover:text-primary/80"
          >
            <Plus />
            Add skill
          </Button>
        }
      >
        <SettingsGroupState
          isLoading={skillsLoading}
          hasError={Boolean(skillsError)}
          isEmpty={skills.length === 0}
          errorMessage="Unable to load your skills."
          emptyMessage="No skills yet."
          emptyHint="Add one for the guidance you find yourself repeating to an agent."
          skeletonRows={2}
        >
          <ul className="divide-y divide-border">
            {skills.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                canEdit
                onEdit={() => handleEditSkill(skill.id)}
                onDelete={() => setDeleteTarget(skill)}
              />
            ))}
          </ul>
        </SettingsGroupState>
      </SettingsGroup>

      <SkillFormDialog
        open={dialogOpen && (dialogContent.kind === "create" || dialogContent.kind === "edit")}
        onClose={closeDialog}
        onSubmit={handleEditorSubmit}
        mode={dialogContent.kind === "edit" ? SkillFormDialogMode.EDIT : SkillFormDialogMode.CREATE}
        initialData={skillFormInitialData}
        duplicateSourceName={dialogContent.kind === "create" ? dialogContent.sourceName : undefined}
        description={
          dialogContent.kind === "create" && dialogContent.sourceName
            ? "Edit before saving — original is unchanged. This copy will be added to your library."
            : "This skill will be added to your library."
        }
      />

      <SkillPickerModal
        open={dialogOpen && dialogContent.kind === "picker"}
        onClose={closeDialog}
        onPick={handlePickFromLibrary}
        onCreateNew={handleCreateNew}
      />

      <ConfirmDestructiveDialog
        target={deleteTarget}
        title="Remove this skill?"
        description={(skill) => <>&ldquo;{skill.name}&rdquo; will be permanently deleted.</>}
        confirmLabel="Remove"
        pendingLabel="Removing…"
        onConfirm={handleDeleteSkillConfirm}
        onClose={() => setDeleteTarget(null)}
      />
    </SettingsPage>
  );
};
